import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Users, Mail, KeyRound, LogOut, Plus, Pencil, Trash2,
  Copy, Check, ExternalLink, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import PageContentEditor from "../components/PageContentEditor";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TABS = [
  { id: "landing", label: "Landing Page", icon: LayoutTemplate },
  { id: "about", label: "Who We Are", icon: LayoutTemplate },
  { id: "connect", label: "Connect", icon: Mail },
  { id: "projects", label: "Projects", icon: LayoutGrid },
  { id: "directors", label: "Directors", icon: Users },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "clients", label: "Client Access", icon: KeyRound },
];

const ABOUT_GROUPS = [
  { title: "Hero", fields: [
    { k: "hero_eyebrow", label: "Eyebrow" },
    { k: "hero_title", label: "Headline", area: true },
    { k: "hero_image", label: "Hero image URL" },
  ]},
  { title: "The Story", fields: [
    { k: "story_eyebrow", label: "Eyebrow" },
    { k: "story_heading", label: "Heading", area: true },
    { k: "story_p1", label: "Paragraph 1", area: true },
    { k: "story_p2", label: "Paragraph 2", area: true },
    { k: "story_p3", label: "Paragraph 3", area: true },
  ]},
  { title: "How We Operate (values)", fields: [
    { k: "values_eyebrow", label: "Eyebrow" },
    { k: "values_heading", label: "Heading" },
    { k: "value_1_title", label: "Value 1 - title" },
    { k: "value_1_body", label: "Value 1 - text", area: true },
    { k: "value_2_title", label: "Value 2 - title" },
    { k: "value_2_body", label: "Value 2 - text", area: true },
    { k: "value_3_title", label: "Value 3 - title" },
    { k: "value_3_body", label: "Value 3 - text", area: true },
    { k: "value_4_title", label: "Value 4 - title" },
    { k: "value_4_body", label: "Value 4 - text", area: true },
  ]},
  { title: "Closing call-to-action", fields: [
    { k: "cta_heading", label: "Heading", area: true },
    { k: "cta_button", label: "Button text" },
  ]},
];

const CONNECT_GROUPS = [
  { title: "Header", fields: [
    { k: "eyebrow", label: "Eyebrow" },
    { k: "title", label: "Headline", area: true },
    { k: "subtitle", label: "Sub-headline", area: true },
  ]},
  { title: "Contact details", fields: [
    { k: "email", label: "Contact email" },
    { k: "online", label: "Online / website label" },
  ]},
];

const DIRECTORS_PAGE_GROUPS = [
  { title: "Page header", fields: [
    { k: "eyebrow", label: "Eyebrow" },
    { k: "title", label: "Headline" },
    { k: "subtitle", label: "Sub-headline", area: true },
  ]},
];

const PROJECTS_PAGE_GROUPS = [
  { title: "Page header", fields: [
    { k: "eyebrow", label: "Eyebrow" },
    { k: "title", label: "Headline" },
    { k: "subtitle", label: "Sub-headline", area: true },
  ]},
];

const SERVICE_EDITOR_GROUPS = [1, 2, 3, 4, 5, 6].map((n) => ({
  title: `Service card ${n}`,
  fields: [
    { k: `service_${n}_kicker`, label: "Kicker (small label)" },
    { k: `service_${n}_title`, label: "Title" },
    { k: `service_${n}_body`, label: "Body", area: true },
    { k: `service_${n}_sub_1`, label: "Sub-service 1" },
    { k: `service_${n}_sub_2`, label: "Sub-service 2" },
    { k: `service_${n}_sub_3`, label: "Sub-service 3" },
    { k: `service_${n}_image`, label: "Image URL (cards 1, 4, 5 show photos)" },
  ],
}));

const HOME_GROUPS = [
  { title: "Hero", fields: [
    { k: "hero_eyebrow", label: "Eyebrow" },
    { k: "hero_title", label: "Headline (start)" },
    { k: "hero_title_accent", label: "Headline (italic accent)" },
    { k: "hero_title_end", label: "Headline (end)" },
    { k: "hero_subtitle", label: "Sub-headline", area: true },
    { k: "hero_image", label: "Hero image URL" },
    { k: "hero_cta_primary", label: "Primary button text" },
    { k: "hero_cta_secondary", label: "Secondary button text" },
  ]},
  { title: "Origin section", fields: [
    { k: "intro_eyebrow", label: "Eyebrow" },
    { k: "intro_heading", label: "Heading", area: true },
    { k: "intro_body", label: "Body", area: true },
  ]},
  { title: "Services section", fields: [
    { k: "services_eyebrow", label: "Eyebrow" },
    { k: "services_heading", label: "Heading" },
  ]},
  ...SERVICE_EDITOR_GROUPS,
  { title: "How We Work (principles)", fields: [
    { k: "principle_1_title", label: "Principle 1 - title" },
    { k: "principle_1_label", label: "Principle 1 - text" },
    { k: "principle_2_title", label: "Principle 2 - title" },
    { k: "principle_2_label", label: "Principle 2 - text" },
    { k: "principle_3_title", label: "Principle 3 - title" },
    { k: "principle_3_label", label: "Principle 3 - text" },
    { k: "principle_4_title", label: "Principle 4 - title" },
    { k: "principle_4_label", label: "Principle 4 - text" },
  ]},
  { title: "Closing call-to-action", fields: [
    { k: "cta_eyebrow", label: "Eyebrow" },
    { k: "cta_heading", label: "Heading", area: true },
    { k: "cta_body", label: "Body", area: true },
    { k: "cta_button", label: "Button text" },
  ]},
];

const emptyProject = { title: "", category: "Energy", location: "", summary: "", description: "", capital: "", status: "active", image_url: "" };
const emptyDirector = { name: "", title: "", expertise: "", bio: "", photo_url: "", order: 0 };
const emptyVip = { name: "VIP Client", user_id: "", password: "" };

const field = "mt-2";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("landing");

  const [projects, setProjects] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);

  const [projDialog, setProjDialog] = useState({ open: false, data: emptyProject, editId: null });
  const [dirDialog, setDirDialog] = useState({ open: false, data: emptyDirector, editId: null });
  const [vipDialog, setVipDialog] = useState({ open: false, data: emptyVip, editId: null });
  const [confirm, setConfirm] = useState({ open: false, onConfirm: null, label: "" });
  const [copied, setCopied] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, d, m, c] = await Promise.all([
        api.get("/projects"),
        api.get("/directors"),
        api.get("/admin/messages"),
        api.get("/admin/clients"),
      ]);
      setProjects(p.data);
      setDirectors(d.data);
      setMessages(m.data);
      setClients(c.data);
    } catch (e) {
      toast.error("Could not load dashboard data.");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const unread = messages.filter((m) => !m.read).length;

  // ---- Projects ----
  const saveProject = async () => {
    const d = projDialog.data;
    if (!d.title || !d.summary) { toast.error("Title and summary are required."); return; }
    setSaving(true);
    try {
      if (projDialog.editId) {
        await api.put(`/admin/projects/${projDialog.editId}`, d);
        toast.success("Project updated.");
      } else {
        await api.post("/admin/projects", d);
        toast.success("Project added.");
      }
      setProjDialog({ open: false, data: emptyProject, editId: null });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deleteProject = (id) =>
    setConfirm({
      open: true, label: "Delete this project?",
      onConfirm: async () => {
        await api.delete(`/admin/projects/${id}`);
        toast.success("Project deleted."); loadAll();
      },
    });

  // ---- Directors ----
  const saveDirector = async () => {
    const d = dirDialog.data;
    if (!d.name || !d.title || !d.bio) { toast.error("Name, title and bio are required."); return; }
    setSaving(true);
    try {
      const payload = { ...d, order: Number(d.order) || 0 };
      if (dirDialog.editId) {
        await api.put(`/admin/directors/${dirDialog.editId}`, payload);
        toast.success("Director updated.");
      } else {
        await api.post("/admin/directors", payload);
        toast.success("Director added.");
      }
      setDirDialog({ open: false, data: emptyDirector, editId: null });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deleteDirector = (id) =>
    setConfirm({
      open: true, label: "Delete this director?",
      onConfirm: async () => {
        await api.delete(`/admin/directors/${id}`);
        toast.success("Director deleted."); loadAll();
      },
    });

  // ---- VIPs ----
  const saveVip = async () => {
    const d = vipDialog.data;
    setSaving(true);
    try {
      if (vipDialog.editId) {
        await api.put(`/admin/clients/${vipDialog.editId}`, d);
        toast.success("VIP updated.");
      } else {
        const { data } = await api.post("/admin/clients", d);
        toast.success(`VIP created: ${data.login_id}`);
      }
      setVipDialog({ open: false, data: emptyVip, editId: null });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deleteVip = (id) =>
    setConfirm({
      open: true, label: "Delete this VIP account?",
      onConfirm: async () => {
        await api.delete(`/admin/clients/${id}`);
        toast.success("VIP deleted."); loadAll();
      },
    });


  // ---- Messages ----
  const markRead = async (id) => { await api.put(`/admin/messages/${id}/read`); loadAll(); };
  const deleteMessage = (id) =>
    setConfirm({
      open: true, label: "Delete this message?",
      onConfirm: async () => {
        await api.delete(`/admin/messages/${id}`);
        toast.success("Message deleted."); loadAll();
      },
    });

  const copy = async (text, key) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      toast.error("Clipboard blocked by your browser. Please copy manually.");
    }
  };

  return (
    <div className="min-h-screen bg-sp-bg" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-sp-navy/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden sm:inline font-mono text-[0.65rem] uppercase tracking-[0.2em] text-sp-soft border-l border-sp-navy/15 pl-4">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-view-site-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full border border-sp-navy/20 px-4 py-2 text-xs text-sp-navy hover:bg-sp-navy/5 transition-colors duration-300"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 rounded-full bg-sp-navy px-4 py-2 text-xs text-white hover:bg-sp-deep transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <p className="sp-eyebrow">Welcome back</p>
        <h1 className="mt-3 font-serif text-3xl md:text-4xl text-sp-deep">{user?.name || "Administrator"}</h1>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-sp-navy/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-5 py-3 font-sans text-sm transition-colors duration-300 ${
                tab === t.id ? "text-sp-navy" : "text-sp-soft hover:text-sp-deep"
              }`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.75} />
              {t.label}
              {t.id === "messages" && unread > 0 && (
                <span className="ml-1 rounded-full bg-sp-navy text-white text-[0.6rem] px-1.5 py-0.5">{unread}</span>
              )}
              {tab === t.id && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-sp-navy" />}
            </button>
          ))}
        </div>

        {/* ---- LANDING PAGE ---- */}
        {tab === "landing" && (
          <div className="mt-10" data-testid="admin-landing-panel">
            <PageContentEditor
              page="home"
              prefix="landing"
              groups={HOME_GROUPS}
              heading="Landing Page Content"
              description="Edit the homepage text and photos. Paste image URLs for photos. Changes are saved to the site."
            />
          </div>
        )}

        {/* ---- WHO WE ARE ---- */}
        {tab === "about" && (
          <div className="mt-10" data-testid="admin-about-panel">
            <PageContentEditor
              page="about"
              prefix="about"
              groups={ABOUT_GROUPS}
              heading="Who We Are Page"
              description="Edit the Who We Are page — hero, story, values and closing call-to-action."
            />
          </div>
        )}

        {/* ---- CONNECT ---- */}
        {tab === "connect" && (
          <div className="mt-10" data-testid="admin-connect-panel">
            <PageContentEditor
              page="connect"
              prefix="connect"
              groups={CONNECT_GROUPS}
              heading="Connect Page"
              description="Edit the Connect page header and the contact details shown to visitors."
            />
          </div>
        )}

        {/* ---- PROJECTS ---- */}
        {tab === "projects" && (
          <div className="mt-10" data-testid="admin-projects-panel">
            <div className="mb-10">
              <PageContentEditor
                page="projects"
                prefix="projectspage"
                groups={PROJECTS_PAGE_GROUPS}
                heading="Projects Page Header"
                description="Edit the heading text shown at the top of the private Projects area."
              />
            </div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-sp-deep">Projects ({projects.length})</h2>
              <button
                data-testid="admin-add-project-btn"
                onClick={() => setProjDialog({ open: true, data: emptyProject, editId: null })}
                className="inline-flex items-center gap-2 rounded-full bg-sp-navy px-5 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors duration-300"
              >
                <Plus className="w-4 h-4" /> Add Project
              </button>
            </div>
            <div className="grid gap-3">
              {projects.map((p) => (
                <div key={p.id} data-testid={`admin-project-row-${p.id}`} className="flex items-center gap-4 bg-white border border-sp-navy/10 rounded-sm p-4">
                  <div className="h-14 w-20 rounded-sm overflow-hidden bg-sp-navy/5 shrink-0">
                    {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-lg text-sp-deep truncate">{p.title}</p>
                    <p className="font-mono text-xs uppercase tracking-wide text-sp-soft">{p.category} · {p.capital}</p>
                  </div>
                  <span className={`shrink-0 font-mono text-[0.65rem] uppercase tracking-wide px-3 py-1 rounded-full ${p.status === "funded" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {p.status}
                  </span>
                  <button data-testid={`admin-edit-project-${p.id}`} onClick={() => setProjDialog({ open: true, data: { ...emptyProject, ...p }, editId: p.id })} className="p-2 text-sp-soft hover:text-sp-navy transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-project-${p.id}`} onClick={() => deleteProject(p.id)} className="p-2 text-sp-soft hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- DIRECTORS ---- */}
        {tab === "directors" && (
          <div className="mt-10" data-testid="admin-directors-panel">
            <div className="mb-10">
              <PageContentEditor
                page="directors"
                prefix="directorspage"
                groups={DIRECTORS_PAGE_GROUPS}
                heading="Directors Page Header"
                description="Edit the heading text shown at the top of the Directors page. Manage individual biosketches below."
              />
            </div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-sp-deep">Directors ({directors.length})</h2>
              <button
                data-testid="admin-add-director-btn"
                onClick={() => setDirDialog({ open: true, data: { ...emptyDirector, order: directors.length }, editId: null })}
                className="inline-flex items-center gap-2 rounded-full bg-sp-navy px-5 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors duration-300"
              >
                <Plus className="w-4 h-4" /> Add Director
              </button>
            </div>
            <div className="grid gap-3">
              {directors.map((d) => (
                <div key={d.id} data-testid={`admin-director-row-${d.id}`} className="flex items-center gap-4 bg-white border border-sp-navy/10 rounded-sm p-4">
                  <div className="h-12 w-12 rounded-full overflow-hidden bg-sp-navy/5 shrink-0 flex items-center justify-center font-serif text-sp-navy">
                    {d.photo_url ? <img src={d.photo_url} alt="" className="h-full w-full object-cover" /> : d.name?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-lg text-sp-deep truncate">{d.name}</p>
                    <p className="font-sans text-sm text-sp-navy truncate">{d.title}</p>
                  </div>
                  <button data-testid={`admin-edit-director-${d.id}`} onClick={() => setDirDialog({ open: true, data: { ...emptyDirector, ...d }, editId: d.id })} className="p-2 text-sp-soft hover:text-sp-navy transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-director-${d.id}`} onClick={() => deleteDirector(d.id)} className="p-2 text-sp-soft hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- MESSAGES ---- */}
        {tab === "messages" && (
          <div className="mt-10" data-testid="admin-messages-panel">
            <h2 className="font-serif text-2xl text-sp-deep mb-6">Inquiries ({messages.length})</h2>
            {messages.length === 0 ? (
              <p className="text-sp-soft font-sans" data-testid="admin-messages-empty">No messages yet. Submissions from the Connect form appear here.</p>
            ) : (
              <div className="grid gap-3">
                {messages.map((m) => (
                  <div key={m.id} data-testid={`admin-message-row-${m.id}`} className={`bg-white border rounded-sm p-5 ${m.read ? "border-sp-navy/10" : "border-sp-navy/30"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3">
                          {!m.read && <span className="h-2 w-2 rounded-full bg-sp-navy" />}
                          <p className="font-serif text-lg text-sp-deep">{m.subject}</p>
                        </div>
                        <p className="mt-1 font-sans text-sm text-sp-soft">
                          {m.name} · <a href={`mailto:${m.email}`} className="text-sp-navy underline underline-offset-2">{m.email}</a>
                          {m.company ? ` · ${m.company}` : ""}
                        </p>
                        <p className="mt-3 font-sans text-sm text-sp-deep/80 whitespace-pre-line">{m.message}</p>
                        <p className="mt-3 font-mono text-[0.65rem] text-sp-soft">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!m.read && (
                          <button data-testid={`admin-message-read-${m.id}`} onClick={() => markRead(m.id)} title="Mark read" className="p-2 text-sp-soft hover:text-emerald-600 transition-colors">
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button data-testid={`admin-message-delete-${m.id}`} onClick={() => deleteMessage(m.id)} className="p-2 text-sp-soft hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---- CLIENTS ---- */}
        {tab === "clients" && (
          <div className="mt-10" data-testid="admin-clients-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h2 className="font-serif text-2xl text-sp-deep">VIP Client Access ({clients.length})</h2>
              <button
                data-testid="admin-add-vip-btn"
                onClick={() => setVipDialog({ open: true, data: emptyVip, editId: null })}
                className="inline-flex items-center gap-2 rounded-full bg-sp-navy px-5 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors duration-300"
              >
                <Plus className="w-4 h-4" /> Add VIP
              </button>
            </div>
            <p className="font-sans text-sm text-sp-soft mb-6 max-w-2xl">
              Credentials for high-net-worth clients and funders. Add, edit or remove VIP access here; leave User ID / Password blank when adding to auto-generate them. Share privately — each grants access to the site.
            </p>
            <div className="overflow-x-auto bg-white border border-sp-navy/10 rounded-sm">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-sp-navy/10 font-mono text-[0.65rem] uppercase tracking-[0.16em] text-sp-soft">
                    <th className="px-5 py-3">#</th>
                    <th className="px-5 py-3">User ID</th>
                    <th className="px-5 py-3">Password</th>
                    <th className="px-5 py-3">Batch</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr key={c.id} data-testid={`admin-client-row-${c.username}`} className="border-b border-sp-navy/5 last:border-0 hover:bg-sp-bg transition-colors">
                      <td className="px-5 py-3 font-mono text-sm text-sp-soft">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-5 py-3 font-sans text-sm text-sp-deep">{c.login_id}</td>
                      <td className="px-5 py-3 font-mono text-sm text-sp-navy">{c.password}</td>
                      <td className="px-5 py-3">
                        <span className={`font-mono text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full ${c.batch === "2025-batch2" ? "bg-sp-navy/10 text-sp-navy" : c.batch === "custom" ? "bg-emerald-100 text-emerald-700" : "bg-sp-earth/15 text-sp-earth"}`}>
                          {c.batch === "2025-batch2" ? "New" : c.batch === "custom" ? "Added" : "Original"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid={`admin-client-copy-${c.username}`}
                            onClick={() => copy(`${c.login_id} / ${c.password}`, c.id)}
                            className="inline-flex items-center gap-1.5 text-xs text-sp-soft hover:text-sp-navy transition-colors px-2"
                          >
                            {copied === c.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button data-testid={`admin-edit-vip-${c.username}`} onClick={() => setVipDialog({ open: true, data: { name: c.name || "VIP Client", user_id: c.login_id, password: "" }, editId: c.id })} className="p-2 text-sp-soft hover:text-sp-navy transition-colors">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button data-testid={`admin-delete-vip-${c.username}`} onClick={() => deleteVip(c.id)} className="p-2 text-sp-soft hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Project Dialog */}
      <Dialog open={projDialog.open} onOpenChange={(v) => setProjDialog((s) => ({ ...s, open: v }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sp-deep">{projDialog.editId ? "Edit Project" : "Add Project"}</DialogTitle>
            <DialogDescription>Complete the fields below. Fields marked * are required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input data-testid="project-form-title" className={field} value={projDialog.data.title} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, title: e.target.value } }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select data-testid="project-form-category" className={`${field} w-full h-10 rounded-md border border-input bg-background px-3 text-sm`} value={projDialog.data.category} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, category: e.target.value } }))}>
                  <option>Energy</option>
                  <option>Aviation</option>
                  <option>Carbon Credits</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select data-testid="project-form-status" className={`${field} w-full h-10 rounded-md border border-input bg-background px-3 text-sm`} value={projDialog.data.status} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, status: e.target.value } }))}>
                  <option value="active">Active</option>
                  <option value="funded">Funded</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input data-testid="project-form-location" className={field} value={projDialog.data.location} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, location: e.target.value } }))} />
              </div>
              <div>
                <Label>Capital</Label>
                <Input data-testid="project-form-capital" className={field} placeholder="USD 100M" value={projDialog.data.capital} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, capital: e.target.value } }))} />
              </div>
            </div>
            <div>
              <Label>Summary *</Label>
              <Textarea data-testid="project-form-summary" className={field} rows={2} value={projDialog.data.summary} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, summary: e.target.value } }))} />
            </div>
            <div>
              <Label>Full description</Label>
              <Textarea data-testid="project-form-description" className={field} rows={4} value={projDialog.data.description} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, description: e.target.value } }))} />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input data-testid="project-form-image" className={field} value={projDialog.data.image_url} onChange={(e) => setProjDialog((s) => ({ ...s, data: { ...s.data, image_url: e.target.value } }))} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="project-form-save" disabled={saving} onClick={saveProject} className="rounded-full bg-sp-navy px-6 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save Project"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Director Dialog */}
      <Dialog open={dirDialog.open} onOpenChange={(v) => setDirDialog((s) => ({ ...s, open: v }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="director-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sp-deep">{dirDialog.editId ? "Edit Director" : "Add Director"}</DialogTitle>
            <DialogDescription>Biosketch details shown on the public Directors page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input data-testid="director-form-name" className={field} value={dirDialog.data.name} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} />
            </div>
            <div>
              <Label>Title *</Label>
              <Input data-testid="director-form-title" className={field} value={dirDialog.data.title} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, title: e.target.value } }))} />
            </div>
            <div>
              <Label>Expertise (eyebrow)</Label>
              <Input data-testid="director-form-expertise" className={field} value={dirDialog.data.expertise} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, expertise: e.target.value } }))} />
            </div>
            <div>
              <Label>Bio *</Label>
              <Textarea data-testid="director-form-bio" className={field} rows={4} value={dirDialog.data.bio} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, bio: e.target.value } }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Photo URL (optional)</Label>
                <Input data-testid="director-form-photo" className={field} value={dirDialog.data.photo_url} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, photo_url: e.target.value } }))} />
              </div>
              <div>
                <Label>Order</Label>
                <Input data-testid="director-form-order" type="number" className={field} value={dirDialog.data.order} onChange={(e) => setDirDialog((s) => ({ ...s, data: { ...s.data, order: e.target.value } }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="director-form-save" disabled={saving} onClick={saveDirector} className="rounded-full bg-sp-navy px-6 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save Director"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIP Dialog */}
      <Dialog open={vipDialog.open} onOpenChange={(v) => setVipDialog((s) => ({ ...s, open: v }))}>
        <DialogContent className="max-w-md" data-testid="vip-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sp-deep">{vipDialog.editId ? "Edit VIP" : "Add VIP"}</DialogTitle>
            <DialogDescription>
              {vipDialog.editId ? "Update this VIP's label, User ID or password." : "Leave User ID and Password blank to auto-generate them."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Label / Name</Label>
              <Input data-testid="vip-form-name" className={field} placeholder="e.g. Acme Family Office" value={vipDialog.data.name} onChange={(e) => setVipDialog((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} />
            </div>
            <div>
              <Label>User ID {vipDialog.editId ? "" : "(optional)"}</Label>
              <Input data-testid="vip-form-userid" className={field} placeholder="Blank = auto VIP2025####" value={vipDialog.data.user_id} onChange={(e) => setVipDialog((s) => ({ ...s, data: { ...s.data, user_id: e.target.value } }))} />
            </div>
            <div>
              <Label>Password {vipDialog.editId ? "(leave blank to keep)" : "(optional)"}</Label>
              <Input data-testid="vip-form-password" className={field} placeholder="Blank = auto-generate" value={vipDialog.data.password} onChange={(e) => setVipDialog((s) => ({ ...s, data: { ...s.data, password: e.target.value } }))} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="vip-form-save" disabled={saving} onClick={saveVip} className="rounded-full bg-sp-navy px-6 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors disabled:opacity-60">
              {saving ? "Saving…" : vipDialog.editId ? "Save Changes" : "Create VIP"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={confirm.open} onOpenChange={(v) => setConfirm((s) => ({ ...s, open: v }))}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.label}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete"
              onClick={async () => { try { await confirm.onConfirm?.(); } catch (e) { toast.error("Action failed."); } }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
